import React from 'react';
import dayjs from 'dayjs';

import { isString } from 'utils/check';
import {
  AnchorPopup,
  type DateType,
  dayJsToRuDate,
  ruDateToDayJs,
  Select,
  type SelectImperativeParams,
  TextField,
  useClickOutside,
} from 'ui';
import { useCalendarMatrix } from 'ui/DatePicker/useCalendarMatrix';
import { ArrowNextIcon, ArrowPrevIcon } from 'ui/icons';

import classes from './DatePicker.module.scss';
import popupClasses from './Popup.module.scss';
import type { DatePickerProps } from './types';
import { useDatePickerView } from './useDatePickerView';

import type { FormValue } from 'form';

const weekDays = ['пн', 'вт', 'ср', 'чт', 'пт', 'cб', 'вс'];

const years: { year: string }[] = [];

for (let i = 2000; i <= 2050; i++) {
  years.push({ year: String(i) });
}

const months = [
  { name: 'Январь', value: '01' },
  { name: 'Февраль', value: '02' },
  { name: 'Март', value: '03' },
  { name: 'Апрель', value: '04' },
  { name: 'Май', value: '05' },
  { name: 'Июнь', value: '06' },
  { name: 'Июль', value: '07' },
  { name: 'Август', value: '08' },
  { name: 'Сентябрь', value: '09' },
  { name: 'Октябрь', value: '10' },
  { name: 'Ноябрь', value: '11' },
  { name: 'Декабрь', value: '12' },
];

const yearOptionsPropsMap: {
  key: 'year';
  title: 'year';
  viewValue: 'year';
  subTitle?: 'year';
  imageSrc?: 'year';
} = {
  key: 'year',
  viewValue: 'year',
  title: 'year',
};

const monthOptionsPropsMap: {
  key: 'value' | 'name';
  title: 'value' | 'name';
  viewValue: 'value' | 'name';
  subTitle?: 'value' | 'name';
  imageSrc?: 'value' | 'name';
} = {
  key: 'value',
  viewValue: 'name',
  title: 'name',
};

const getInitialYearValue = (date: DateType) => [date.date.format('YYYY')];
const getInitialMonthValue = (date: DateType) => [String(date.date.format('MM'))];

export const DatePickerInternal: React.FC<DatePickerProps> = (props) => {
  const { value, onChange, readOnly, disabled } = props;

  const {
    matrix: calendarMatrix,
    go,
    goPrev,
    goNext,
    goYear,
    goMonth,
    currentDate,
  } = useCalendarMatrix({
    initialDate: {
      date: value ? dayjs(value) : dayjs(),
      meta: {},
    },
  });

  const [popupOpened, setPopupOpened] = React.useState(false);
  const [viewInputValue, setViewInputValue] = React.useState('');
  const inputContainerRef = React.useRef<HTMLDivElement>(null);
  const popupContainerRef = React.useRef<HTMLDivElement>(null);
  const yearImperativeParamsRef = React.useRef<SelectImperativeParams>({});
  const monthImperativeParamsRef = React.useRef<SelectImperativeParams>({});

  const handleOpen = React.useCallback(() => {
    if (disabled) return;
    setPopupOpened(true);
  }, [disabled]);

  const handleClear = React.useCallback(() => {
    go({ date: dayjs(), meta: {} });
    setViewInputValue('');
    onChange?.('');
  }, []);

  React.useEffect(() => {
    if (value) setViewInputValue(dayjs(value).format('DD.MM.YYYY'));
  }, [value]);

  const {
    textFieldProps,
    popupContainerProps,
    popupPaperProps,
    pickerMonthContainerProps,
    pickerHeaderProps,
    pickerMonthProps,
    pickerWeekDayProps,
    pickerDayProps,
    pickerArrowsContainerProps,
    pickerArrowProps,
  } = useDatePickerView(props, classes, {
    openIconOnClick: handleOpen,
    clearIconOnClick: handleClear,
    popupOpened,
  });

  const clickOutsideIgnore = [inputContainerRef, popupContainerRef];

  if (yearImperativeParamsRef.current) {
    if (yearImperativeParamsRef.current.inputContainerRef) {
      clickOutsideIgnore.push(yearImperativeParamsRef.current.inputContainerRef);
    }

    if (yearImperativeParamsRef.current.popupContainerRef) {
      clickOutsideIgnore.push(yearImperativeParamsRef.current.popupContainerRef);
    }
  }

  if (monthImperativeParamsRef.current) {
    if (monthImperativeParamsRef.current.inputContainerRef) {
      clickOutsideIgnore.push(monthImperativeParamsRef.current.inputContainerRef);
    }

    if (monthImperativeParamsRef.current.popupContainerRef) {
      clickOutsideIgnore.push(monthImperativeParamsRef.current.popupContainerRef);
    }
  }

  useClickOutside(clickOutsideIgnore, () => {
    setPopupOpened(false);
  });

  const changeYear = (value: FormValue) => {
    if (isString(value)) goYear(value);
  };

  const changeMonth = (value: FormValue) => {
    if (isString(value)) goMonth(value);
  };

  const changeDay = (value: DateType) => {
    go(value);
    onChange?.(dayjs(value.date).format('YYYY-MM-DD'));
    setViewInputValue(dayJsToRuDate(value.date));
    setPopupOpened(false);
  };

  const handleInputOnChangeDate = (value: string) => {
    const dayjsValue = ruDateToDayJs(value);
    go({ date: dayjsValue, meta: {} });
    onChange?.(value ? dayjsValue.format('YYYY-MM-DD') : '');
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        value={viewInputValue}
        inputContainerRef={inputContainerRef}
        mask={'ru-date'}
        onChange={readOnly ? undefined : handleInputOnChangeDate}
      />
      <AnchorPopup
        ref={popupContainerRef}
        anchorRef={inputContainerRef}
        enabled={readOnly ? false : popupOpened}
        classes={popupClasses}
      >
        <div className={popupContainerProps.className}>
          <div className={popupPaperProps.className}>
            <div className={pickerHeaderProps.className}>
              <div style={{ width: '58px' }}>
                <Select
                  options={years}
                  optionPropsMap={yearOptionsPropsMap}
                  variant={'clear'}
                  size={'clear'}
                  value={getInitialYearValue(currentDate)}
                  imperativeParams={yearImperativeParamsRef}
                  popupMinWidth={90}
                  popupMaxHeight={200}
                  onChange={changeYear}
                  widthFullSize
                />
              </div>
              <div style={{ width: '87px' }}>
                <Select
                  options={months}
                  optionPropsMap={monthOptionsPropsMap}
                  variant={'clear'}
                  size={'clear'}
                  value={getInitialMonthValue(currentDate)}
                  imperativeParams={monthImperativeParamsRef}
                  popupMinWidth={90}
                  popupMaxHeight={200}
                  onChange={changeMonth}
                  widthFullSize
                />
              </div>
              <div className={pickerArrowsContainerProps.className}>
                <div onClick={goPrev} className={pickerArrowProps.className}>
                  <ArrowPrevIcon />
                </div>
                <div onClick={goNext} className={pickerArrowProps.className}>
                  <ArrowNextIcon />
                </div>
              </div>
            </div>
            <div className={pickerMonthContainerProps.className}>
              {calendarMatrix.map((month) => {
                return (
                  <div key={month.meta.key} className={pickerMonthProps.className}>
                    {weekDays.map((day) => {
                      return (
                        <div key={day} className={pickerWeekDayProps.className}>
                          {day}
                        </div>
                      );
                    })}
                    {month.days.map((day) => {
                      return (
                        <div
                          key={day.meta.key}
                          className={pickerDayProps.getClassName(day)}
                          onClick={() => changeDay(day)}
                        >
                          {day.date.format('D')}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </AnchorPopup>
    </>
  );
};

export const DatePicker = React.memo(DatePickerInternal);
